/** The table's columns, in order. */
export const IMPORT_TABLE_COLUMN = {
    row: 'row',
    status: 'status',
    name: 'name',
    sku: 'sku',
    barcode: 'barcode',
    lotNumber: 'lotNumber',
    attributes: 'attributes',
    quantity: 'quantity',
    cost: 'cost',
    salePrice: 'salePrice',
    match: 'match',
    note: 'note',
    actions: 'actions',
} as const;

/**
 * The actions column: the row's Edit and Exclude / Include, two small
 * icon-only buttons (32 px each, 4 px apart) inside the cell's 16 px
 * padding each side.
 */
export const IMPORT_TABLE_ACTIONS_WIDTH_PX = 100;

/**
 * Barcode and Lot number (bos-app#222): wide enough for a 15-digit IMEI
 * whole inside the cell's 16 px padding each side, the longest code the file
 * usually has in either; a longer one truncates with the full code as its
 * title.
 */
export const IMPORT_TABLE_CODE_WIDTH_PX = 148;

/**
 * The note's minimum (bos-app#222; 84 before): about 140 px of its sentence
 * beside the icon and Fix, enough to tell one problem from another; the
 * whole sentence is a press away (`NoteCell`). With the code columns the
 * grid is wider than a laptop anyway, so the note no longer gives way to
 * fit one.
 */
export const IMPORT_TABLE_NOTE_MIN_WIDTH_PX = 220;

/**
 * Fixed heights: virtualization renders only the rows that fit the viewport,
 * and computes which from these. A row holds one line of text and a small
 * chip.
 */
export const IMPORT_TABLE_ROW_HEIGHT_PX = 44;
export const IMPORT_TABLE_HEADING_HEIGHT_PX = 36;

/**
 * The grid's scroll viewport. Virtualization needs a bounded height, so the
 * rows scroll inside the table rather than growing the page.
 *
 * - **Height:** the viewport less what the wizard puts around the grid (top
 *   bar, step strip, branch line, the step's header, the tabs, the table
 *   footer, the action bar and the paddings: 564 px at 1,440 px wide and
 *   574 at 1,280, the tabs' 60 and the action bar's 84 of it), so the page
 *   itself does not scroll on a laptop (1,280 × 800 included); never under
 *   14rem (below that the page scrolls and the action bar sticks to the
 *   bottom).
 * - **Width:** the columns' minimums added up (1,622 px since Barcode and
 *   Lot number joined them and the note's minimum grew, bos-app#222; 1,190
 *   before). The grid's scroll container is 87 px less than the window
 *   (1,353 px at 1,440: the wizard's column padding and the grid's own
 *   vertical scrollbar, 15 to 17 px), so from about 1,710 px wide the grid
 *   fits; anything narrower, a 1,280 or 1,440 px laptop included, scrolls
 *   sideways inside the table, never the page (phones get cards instead,
 *   #189).
 */
export const IMPORT_TABLE_CONTENT_CLASS =
    'h-[calc(100vh-36rem)] min-h-[14rem] min-w-[1622px] overflow-auto';

/**
 * Quantities as the file has them, grouped like money (`1,200`), fractions
 * kept: a fractional quantity is the row's problem and has to show as typed.
 */
const QUANTITY_FORMAT = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 3,
});

export const formatQuantity = (quantity: number): string =>
    QUANTITY_FORMAT.format(quantity);
